import React, { useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Sparkles, MessageSquare, Image as ImageIcon, Zap, Activity, Settings2, Key, BarChart3, Database } from "lucide-react";

export default function AiServices() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Overview");

  // Mock AI Services Data
  const services = [
    {
      id: 1,
      name: "Text Generation (GPT-4)",
      category: "Language Model",
      status: "Active",
      latency: "1.2s",
      usage: "450k Tokens",
      icon: MessageSquare,
      color: "bg-blue-50 text-blue-600 border-blue-200"
    },
    {
      id: 2,
      name: "Image Generation (DALL-E 3)",
      category: "Vision Model",
      status: "Active",
      latency: "4.5s",
      usage: "1,200 Images",
      icon: ImageIcon,
      color: "bg-purple-50 text-purple-600 border-purple-200"
    },
    {
      id: 3,
      name: "Content Moderation API",
      category: "Safety",
      status: "Active",
      latency: "0.3s",
      usage: "2.1M Requests",
      icon: Activity,
      color: "bg-emerald-50 text-emerald-600 border-emerald-200"
    },
    {
      id: 4,
      name: "Voice Synthesis (TTS)",
      category: "Audio",
      status: "Maintenance",
      latency: "-",
      usage: "0 Minutes",
      icon: Zap,
      color: "bg-orange-50 text-orange-600 border-orange-200"
    },
    {
      id: 5,
      name: "Data Analytics Engine",
      category: "Analysis",
      status: "Active",
      latency: "2.1s",
      usage: "154 Queries",
      icon: Database,
      color: "bg-indigo-50 text-indigo-600 border-indigo-200"
    }
  ];

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
      {/* Ambient background blobs */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-400/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-400/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="AI Services"
          subtitle="Manage AI models, API keys, and monitor usage across your applications."
        />

        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
          {/* Header Section */}
          <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-center justify-end gap-4">

            <div className="flex items-center gap-3 w-full lg:w-auto">
              <button className="flex-1 lg:flex-none justify-center bg-white dark:bg-slate-800 hover:bg-slate-50 text-slate-700 border border-slate-200 dark:border-slate-700 shadow-sm px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
                <Key size={16} />
                API Keys
              </button>
              <button className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2 rounded-xl text-sm font-medium hover:-translate-y-0.5 transition-all flex items-center gap-2">
                <Plus size={16} />
                Connect Service
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white dark:bg-slate-800/70 backdrop-blur-xl rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Activity size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Total API Calls</p>
                <h4 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">2.4M</h4>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800/70 backdrop-blur-xl rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <Zap size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Tokens Consumed</p>
                <h4 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">450,201</h4>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800/70 backdrop-blur-xl rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <BarChart3 size={24} />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Avg. Latency</p>
                <h4 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">1.8s</h4>
              </div>
            </div>
          </div>

          {/* Toolbar & Tabs */}
          <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-white dark:bg-slate-800/50 backdrop-blur-md p-1 rounded-xl border border-white w-full sm:w-auto overflow-x-auto shadow-sm">
              {["Overview", "Language Models", "Vision", "Usage Logs"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab
                      ? "bg-white dark:bg-slate-800 text-blue-600 shadow-sm border border-slate-100 dark:border-slate-700"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-700 hover:bg-white dark:bg-slate-800/50"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search AI services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white dark:bg-slate-800/80 backdrop-blur-md border border-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder-slate-400"
              />
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto pb-8">
            {services.map((service) => (
              <div key={service.id} className="bg-white dark:bg-slate-800/70 backdrop-blur-xl rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50 overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all group flex flex-col">
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${service.color}`}>
                      <service.icon size={24} />
                    </div>
                    <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wide uppercase ${service.status === 'Active' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                        'bg-orange-50 text-orange-600 border border-orange-100'
                      }`}>
                      {service.status}
                    </span>
                  </div>

                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{service.category}</span>
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 leading-snug mb-4 group-hover:text-blue-600 transition-colors">
                    {service.name}
                  </h3>

                  <div className="mt-auto pt-4 border-t border-slate-100 dark:border-slate-700/60 grid grid-cols-2 gap-4 text-sm text-slate-500 dark:text-slate-400">
                    <div className="flex flex-col">
                      <span className="text-xs text-slate-400 mb-0.5">Latency</span>
                      <span className="font-medium text-slate-700">{service.latency}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-slate-400 mb-0.5">Usage</span>
                      <span className="font-medium text-slate-700">{service.usage}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex items-center justify-between gap-2">
                    <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors border border-slate-100 dark:border-slate-700">
                      <Settings2 size={16} /> Configure
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
